<script setup lang="ts">
import { computed } from 'vue';
import { UUID } from '@hfc/common';
import { formatHelpers } from '@hfc/ui-core';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { SmallBusinessPlan } from '@/application/smallBusiness/domain/SmallBusinessPlan';
import { ContributionType } from '@/application/smallBusiness/domain/SmallBusinessContribution';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';

const { smallBusinessStore, smallBusinessState } = useSmallBusinessStore();

const { formatPrice } = formatHelpers();

const props = defineProps<{
  plan: SmallBusinessPlan;
  applicationType: APPLICATION_TYPES;
  contributionType?: ContributionType;
  employeeContribution?: number;
  dependentContribution?: number;
}>();

const findEmployeeDataName = (id: UUID): string => {
  const employee = smallBusinessState.value.application.applicants.find((employee) => employee.id === id);
  if (!employee || (!employee.first_name && !employee.last_name)) {
    return '';
  }
  if (employee.first_name && employee.last_name) {
    return `${employee.first_name} ${employee.last_name.charAt(0)}.`;
  }

  return (employee.first_name || employee.last_name) ?? '';
};

const orderedPriceBreakdown = computed(() => {
  const applicants = smallBusinessState.value.application.applicants;

  return applicants
    .map(applicant => props.plan.price_breakdown.find(price_breakdown => price_breakdown.id === applicant.id))
    .filter((item): item is NonNullable<typeof item> => item !== undefined);
});

const totalCost = computed(() => {
  return orderedPriceBreakdown.value.reduce((acc, price_breakdown) => acc + price_breakdown.rate, 0);
});

const getContributionSuffix = (amount?: number): string => {
  return props.contributionType === ContributionType.Flat ? `$${amount || 0}` : `${amount || 0}%`;
};

</script>

<template>
  <div class="flex-col sm:max-h-[calc(100vh-230px)] w-full max-w-full text-sm sm:text-md">
    <div class="flex flex-col px-4 mt-2 py-2 bg-blue-100 rounded-lg">
      <div class="flex flex-col sm:flex-row">
        <div class="flex font-semibold text-blue-600 w-full sm:w-1/3 py-2">Employer Contribution:</div>
        <div class="flex w-full sm:w-1/3 py-2 sm:justify-center">Employee: {{ getContributionSuffix(employeeContribution) }}</div>
        <div class="flex w-full sm:w-1/3 py-2 sm:justify-end">Dependent: {{ getContributionSuffix(dependentContribution) }}</div>
      </div>
    </div>
    <div class="grid overflow-y-auto">
      <table class="grid sm:table w-full min-w-full border border-blue-100 rounded-lg mt-6 mb-4 overflow-x-auto">
        <thead>
          <tr class="border-b border-blue-100">
            <th class="px-4 py-2">Employee Name</th>
            <th class="px-4 py-2">Coverage</th>
            <th class="px-4 py-2 text-right">Employer Cost</th>
            <th class="px-4 py-2 text-right">Employee Cost</th>
            <th class="px-4 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(employeePrice, index) in orderedPriceBreakdown" :key="index" class="border-b border-blue-100 even:bg-white odd:bg-blue-50">
            <td class="px-4 py-2">{{ findEmployeeDataName(employeePrice.id) }}</td>
            <td class="px-4 py-2">{{ smallBusinessStore.findEmployeeDataCoverage(employeePrice.id) }}</td>
            <td class="px-4 py-2 text-right">{{ formatPrice(employeePrice.employer_cost) }}</td>
            <td class="px-4 py-2 text-right">{{ formatPrice(employeePrice.employee_cost) }}</td>
            <td class="px-4 py-2 text-right">{{ formatPrice(employeePrice.rate) }}</td>
          </tr>
          <tr class="border-b border-blue-100 bg-slate-200">
            <td class="px-4 py-2 font-medium text-right lg:whitespace-nowrap" colspan="3">Estimated Total Employer Contribution: <span class="ml-6 font-bold">{{ formatPrice(plan.totalEmployerContribution) }}</span></td>
            <td class="px-4 py-2 text-right font-medium" colspan="2">Estimated Total Cost:  <span class="ml-6 font-bold">{{ formatPrice(totalCost) }}</span></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
